import { IconWithTextLockup, NavList } from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { telemetry } from '../../../../../services/backend';
import { ROUTE_WEATHER_WARNING_DETAILS } from '../../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../../services/route/route-navigation';
import { getWarnings } from '../../../../../services/weather';
import {
  getWeatherAlertIcon,
  ReactSvg,
} from '../../../../shared/vector-images';
import { trackWeatherForecastWarningSelected } from '../../helpers/analytics/weatherForecastAnalytics';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';

const WARNINGS_LOADING_TITLE = 'Checking for warnings...';
const WARNINGS_ERROR_TITLE = 'Warnings unavailable';

export interface WeatherWarning {
  id: string | number;
  title: string;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const formatWarningTimestamp = (warning: WeatherWarning): string => {
  if (warning.updated_at) {
    return `Updated ${DateTime.fromISO(warning.updated_at).toRelative()}`;
  }

  if (warning.created_at) {
    return `Created ${DateTime.fromISO(warning.created_at).toRelative()}`;
  }

  return '';
};

const formatWarningsTitle = (count: number): string =>
  `${count} active warning${count === 1 ? '' : 's'}`;

interface WeatherWarningRowProps {
  warning: WeatherWarning;
  latitude: number;
  longitude: number;
}

const WeatherWarningRow: FC<WeatherWarningRowProps> = ({
  warning,
  latitude,
  longitude,
}) => {
  const theme = useTheme();
  const openDetails = useCallback(() => {
    trackWeatherForecastWarningSelected({
      warningId: warning.id,
      warningType: warning.type,
    });
    transitionToNextRoute(ROUTE_WEATHER_WARNING_DETAILS, {
      id: warning.id,
      originalLatitude: latitude,
      originalLongitude: longitude,
    });
  }, [latitude, longitude, warning.id, warning.type]);

  return (
    <NavList.Item
      chevron
      onClick={openDetails}
      data-testid={`weather-forecast-warning-${warning.id}`}
    >
      <IconWithTextLockup
        flex="1 1 0px"
        emphasis="heading"
        image={
          <ReactSvg
            toDisplay={getWeatherAlertIcon(warning.type)}
            options={{
              fillColor: theme.colors.danger,
            }}
            width={38}
            height={38}
          />
        }
        heading={warning.title}
        description={formatWarningTimestamp(warning)}
      />
    </NavList.Item>
  );
};

export interface WarningsProps {
  latitude: number;
  longitude: number;
}

const Warnings: FC<WarningsProps> = ({ latitude, longitude }) => {
  const [warnings, setWarnings] = useState<WeatherWarning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadWarnings = async () => {
      setIsLoading(true);
      setError(null);
      setWarnings([]);

      try {
        const data = await getWarnings(latitude, longitude);
        if (isMounted) {
          setWarnings(data ?? []);
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[Warnings] Error loading warnings ${JSON.stringify(err)}`,
        );
        if (isMounted) {
          setError('There was a problem loading weather warnings.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWarnings();

    return () => {
      isMounted = false;
    };
  }, [latitude, longitude]);

  /**
   * Every state renders the same header-only card so the cards below never
   * shift when warnings resolve — the loading state lives in the title.
   */
  if (isLoading) {
    return (
      <CollapsibleCard
        title={WARNINGS_LOADING_TITLE}
        testId="weather-forecast-warnings"
        titleTestId="weather-forecast-warnings-loading"
        titleTone="muted"
        ariaBusy
        collapsible={false}
      />
    );
  }

  if (warnings.length === 0) {
    if (error) {
      return (
        /*
         * Keyed so React mounts a fresh node rather than reusing the loading
         * card — role="alert" only announces on insertion, and this replaces
         * the focus-on-mount announcement MessageBlock used to give us.
         */
        <CollapsibleCard
          key="weather-forecast-warnings-error"
          title={WARNINGS_ERROR_TITLE}
          testId="weather-forecast-warnings"
          titleTone="muted"
          cardRole="alert"
          collapsible={false}
        />
      );
    }

    return (
      <CollapsibleCard
        title="No active warnings"
        testId="weather-forecast-warnings"
        collapsible={false}
      />
    );
  }

  const title = formatWarningsTitle(warnings.length);

  return (
    <CollapsibleCard
      title={title}
      testId="weather-forecast-warnings"
      analyticsCard="warnings"
      defaultExpanded
    >
      <NavList edge>
        {warnings.map((warning) => (
          <WeatherWarningRow
            key={warning.id}
            warning={warning}
            latitude={latitude}
            longitude={longitude}
          />
        ))}
      </NavList>
    </CollapsibleCard>
  );
};

export default Warnings;
